import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Tag, Modal, Tabs } from 'antd';
import { LogoutOutlined, MenuOutlined, UserOutlined, MobileOutlined, TabletOutlined, DesktopOutlined, DownloadOutlined, AppleOutlined, AndroidOutlined, MoreOutlined, ShareAltOutlined, PlusSquareOutlined, MessageOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Badge } from 'antd';
import logo from '../../assets/tecnocom-logo.png';
import useDeviceDetection from '../../hooks/deviceDetection.hook';
import { DataService } from '../../services/http-interceptor.service';
import Notifications from '../organisms/notifications/notifications.component';

type Props = {
  user: {
    name: string;
    email: string;
  };
  onLogout: () => void;
  onToggleMenu?: () => void;
};

const HeaderBar: React.FC<Props> = ({ user, onLogout, onToggleMenu }) => {
  const [open, setOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const deviceInfo = useDeviceDetection();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await DataService.get('commentProyect/unread-counts');
        setUnreadCount(response.data.total || 0);
      } catch (error) {
        console.error('Error fetching unread counts:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    window.addEventListener('updateUnreadCounts', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('updateUnreadCounts', fetchUnreadCount);
    };
  }, []);

  const handleLogoutAndClose = () => {
    setOpen(false);
    onLogout();
  };

  const getDeviceInfo = () => {
    if (deviceInfo.isIPad) {
      return {
        icon: <TabletOutlined />,
        text: 'iPad',
        color: 'blue'
      };
    } else if (deviceInfo.isMobile) {
      return {
        icon: <MobileOutlined />,
        text: 'Móvil',
        color: 'green'
      };
    } else if (deviceInfo.isTablet) {
      return {
        icon: <TabletOutlined />,
        text: 'Tablet',
        color: 'orange'
      };
    } else {
      return {
        icon: <DesktopOutlined />,
        text: 'Escritorio',
        color: 'purple'
      };
    }
  };

  const deviceDisplay = getDeviceInfo();

  const items: MenuProps['items'] = [
    {
      type: 'group',
      label: (
        <>
          <span>{user.name}</span>
          <br />
          <span>{user.email}</span>
        </>
      ),
      children: [
        { type: 'divider' },
        {
          key: 'logout',
          label: (
            <span onClick={handleLogoutAndClose}>
              <LogoutOutlined /> Cerrar sesión
            </span>
          ),
        },
      ],
    },
  ];

  return (
    <div className="pwa-header">
      <div className="pwa-header__left">
        {onToggleMenu && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onToggleMenu}
            className="pwa-header__menu-button"
          />
        )}
        {/* Clicable: como en cualquier web, el logo regresa al menú
            principal (/admin). Navegación client-side para no recargar
            toda la PWA. */}
        <img
          src={logo}
          alt="Tecnocom — Ir al inicio"
          className="pwa-header__logo"
          onClick={() => navigate('/admin')}
          style={{ cursor: 'pointer' }}
        />
      </div>

      <div className="pwa-header__right">
        <Notifications user={user} />

        {deviceInfo.isMobile ? (
          <Button
            type="primary"
            shape="circle"
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => setShowInstallModal(true)}
            title="Instalar App"
          />
        ) : (
          <Button
            type="primary"
            shape="round"
            icon={<DownloadOutlined />}
            size="small"
            style={{ marginRight: 8 }}
            onClick={() => setShowInstallModal(true)}
          >
            Instalar App
          </Button>
        )}
        <div className="pwa-header__device-info">
          <Tag
            icon={deviceDisplay.icon}
            color={deviceDisplay.color}
            style={{
              marginRight: 8,
              fontSize: '12px',
              padding: '2px 8px',
              borderRadius: '12px'
            }}
          >
            {deviceDisplay.text}
          </Tag>
        </div>
        <Dropdown
          menu={{ items }}
          trigger={['click']}
          open={open}
          onOpenChange={setOpen}
        >
          <Button
            shape="circle"
            icon={<UserOutlined />}
            className="pwa-header__avatar"
            style={{ padding: 20 }}
          />
        </Dropdown>
      </div>

      <Modal
        title="Instalar Aplicación"
        open={showInstallModal}
        onCancel={() => setShowInstallModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowInstallModal(false)}>
            Entendido
          </Button>
        ]}
      >
        <Tabs
          defaultActiveKey={deviceInfo.isIOS ? "ios" : "android"}
          items={[
            {
              key: 'android',
              label: <span><AndroidOutlined /> Android</span>,
              children: (
                <div style={{ padding: '10px' }}>
                  <ol style={{ lineHeight: '2' }}>
                    <li>Abre el menú de opciones del navegador (usualmente 3 puntos <MoreOutlined />).</li>
                    <li>Busca y selecciona la opción <strong>"Instalar aplicación"</strong> o <strong>"Agregar a pantalla principal"</strong>.</li>
                    <li>Confirma la instalación.</li>
                  </ol>
                </div>
              ),
            },
            {
              key: 'ios',
              label: <span><AppleOutlined /> iOS</span>,
              children: (
                <div style={{ padding: '10px' }}>
                  <ol style={{ lineHeight: '2' }}>
                    <li>Presiona el botón de <strong>Compartir</strong> <ShareAltOutlined /> en la barra inferior.</li>
                    <li>Desliza hacia abajo y selecciona <strong>"Agregar a Inicio"</strong> <PlusSquareOutlined />.</li>
                    <li>Presiona <strong>"Agregar"</strong> en la esquina superior derecha.</li>
                  </ol>
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default HeaderBar;
